import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild,
  WritableSignal,
  afterNextRender,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChildren,
} from '@angular/core';
//import { Clipboard } from '@angular/cdk/clipboard';
import { CurrencyPipe, DecimalPipe, PercentPipe } from '@angular/common';
import { GetByRowColPipe } from '../../../pipes/get-by-row-col.pipe';
import {
  AksiaValidatorFn,
  ISpreadsheetSettings,
  IUiSpreadSheetCell,
  numericExp,
  PropExtend,
  SPREADSHEET_ALLOWED_VALUES,
  SpreadsheetSettings,
  StateEnum,
  UiSpreadSheetCell,
  UTILS,
} from '@aksia/infrastructure';
import { UiValidationDirective } from '../../../directives/ui/ui-validation.directive';
import { UiControlDirective } from '../../../directives/ui/ui-control.directive';

//#region Enums, Types & Consts

const allowedValues = {
  numeric: numericExp,
  alpha: /^[a-z]*$/i,
  all: /^[a-z0-9]*$/i,
} as const;

const allowedKeys = [
  ...UTILS.CONSTANTS.SIGNED_DECIMALS,
  ...UTILS.CONSTANTS.NAVIGATION_KEYS,
  ...UTILS.CONSTANTS.EDIT_KEYS,
  ...UTILS.CONSTANTS.SHORTCUT_NUMBER_KEYS,
  'c',
  'v',
];

const ELEMENT_TYPE = {
  TD: 'td',
  INPUT: 'input',
};
type ElementType = keyof typeof ELEMENT_TYPE;

//#endregion

@Component({
  selector: 'spreadsheet',
  hostDirectives: [
    {
      directive: UiValidationDirective,
      inputs: ['validators', 'validation.id'],
    },
  ],
  imports: [GetByRowColPipe],
  templateUrl: './spreadsheet.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpreadsheetComponent<T = unknown> extends UiControlDirective {
  //#region Injections

  //private clipboard = inject(Clipboard);

  //#endregion

  //#region Inputs/Outpus

  override settings = input<SpreadsheetSettings<T>, ISpreadsheetSettings<T>>(
    new SpreadsheetSettings(),
    {
      transform: (value: ISpreadsheetSettings<T>) => this.initSettings(value),
    },
  );

  data = input([], {
    transform: (data: Array<T>) => {
      this.bindDataToCells(data);
      return data;
    },
  });

  dataSummary = input(undefined, {
    transform: (data: Array<T>) => {
      this.bindDataToSummaryCells(data);
      return data;
    },
  });

  dataValidationGroupId = input<number | undefined>();

  cellValidators = input(undefined, {
    transform: (
      validators:
        | undefined
        | ((cellDataItem: PropExtend<unknown>) => Array<AksiaValidatorFn>),
    ) => {
      return validators;
    },
  });

  @Output() cellSelected: EventEmitter<Array<IUiSpreadSheetCell<T>>> =
    new EventEmitter();

  valueChange = output<{ values: Array<unknown>; dataItem: Array<T> }>();

  //#endregion

  //#region Props

  protected cells: WritableSignal<Array<UiSpreadSheetCell<T>>> = signal([]);
  protected summaryCells: WritableSignal<Array<UiSpreadSheetCell<T>>> = signal(
    [],
  );
  public firstMarkedCell?: IUiSpreadSheetCell<T>;
  public lastMarkedCell?: IUiSpreadSheetCell<T>;
  private allowedValueRegExp!: RegExp;
  private cancelledEditCell?: IUiSpreadSheetCell<T>;
  private keymap: any = {
    td: {
      ArrowLeft: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.updateMarked(0, -1, e),
      ArrowRight: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.updateMarked(0, 1, e),
      ArrowUp: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.updateMarked(-1, 0, e),
      ArrowDown: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.updateMarked(1, 0, e),
      Enter: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.updateMarked(1, 0, e),
      Tab: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.updateMarked(0, e.shiftKey ? -1 : 1, e),
      Home: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.updateMarked(0, -item.columnIndex!, e),
      End: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.updateMarked(
          0,
          this.settings().defaultColumnQuantity! - 1 - item.columnIndex!,
          e,
        ),
      Delete: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.deleteValues(),
      Backspace: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.editCell(item, ''),
      F2: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.editCell(item),
      c: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) => this.copy(e),
      v: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) => {
        if (e.ctrlKey || e.metaKey) {
          this.paste(e);
        }
      },
      Other: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) => {
        if (UTILS.CONSTANTS.SIGNED_DECIMALS.includes(e.key)) {
          this.editCell(item, e.key);
        }
      },
    },
    input: {
      Enter: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.updateMarked(1, 0, e, true),
      Escape: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.undoEdit(e.target as HTMLInputElement, item),
      Tab: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) =>
        this.updateMarked(0, e.shiftKey ? -1 : 1, e),
      Other: (item: IUiSpreadSheetCell<T>, e: KeyboardEvent) => {
        if (UTILS.CONSTANTS.SHORTCUT_NUMBER_MULTIPLIERS[e.key] !== undefined) {
          this.multiplyValue(e, item, e.key);
        }
      },
    },
  };
  private static currencyPipe = new CurrencyPipe('en-US');
  private static percentPipe = new PercentPipe('en-US');
  private static decimalPipe = new DecimalPipe('en-US');

  //#endregion

  //#region View Children

  @ViewChild('uiValueRef') uiValueRef!: ElementRef;
  validations = viewChildren<UiValidationDirective>(UiValidationDirective);

  //#endregion

  //#region Functions
  constructor() {
    super();
    //this.uiValidationDir.host = this;
    afterNextRender(() => {
      if (this.stateService && !this.settings().stateless) {
        if (!this.tag()) {
          throw new Error(
            'Tag or Label is required for state management on:' +
              this.settings().label,
          );
        }
      }
    });
  }

  protected override initSettings(settings: ISpreadsheetSettings<T>) {
    let spreadsheetSettings = UTILS.OBJECT.assign(
      new SpreadsheetSettings<T>(),
      settings,
    ) as SpreadsheetSettings<T>;

    super.initSettings(settings);
    this.onSettingsUpdate();
    return spreadsheetSettings;
  }

  protected onSettingsUpdate() {
    this.allowedValueRegExp =
      allowedValues[
        this.settings().allowedValues ?? SPREADSHEET_ALLOWED_VALUES.Numeric
      ];
  }
  private generateCells(
    columnTitles: Array<string>,
    rowTitles: Array<string>,
    tagPrefix?: string,
    defaultSelectedCell?: string,
  ) {
    this.cells.set([]);
    rowTitles?.forEach((rowTitle, rowIndex) => {
      columnTitles?.forEach((colTitle, colIndex) => {
        let cell = new UiSpreadSheetCell<T>();
        cell.columnIndex = colIndex;
        cell.rowIndex = rowIndex;
        cell.tag = `${tagPrefix ?? 'tag'} ${colTitle}-${rowTitle}`;
        cell.classes = computed(() => 'text-right justify-center');
        cell.selected = defaultSelectedCell === cell.tag;
        this.cells.update((cells) => {
          cells.push(cell);
          return cells;
        });
      });
    });
  }

  private generateSummaryCells(
    columnTitles: Array<string>,
    rowTitles: Array<string>,
  ) {
    let colIndex = columnTitles.length;
    this.summaryCells.set([]);
    rowTitles?.forEach((rowTitle, rowIndex) => {
      let cell = new UiSpreadSheetCell<T>();
      cell.columnIndex = colIndex;
      cell.rowIndex = rowIndex;
      cell.classes = computed(() => 'text-right justify-center');
      this.summaryCells.update((summaryCells) => {
        summaryCells.push(cell);
        return summaryCells;
      });
    });
  }

  private bindDataToCells(data: Array<T>) {
    this.generateCells(
      this.settings().columnTitles!,
      this.settings().rowTitles!,
    );

    if (data) {
      let dataItemIsObject = data?.some((item) => UTILS.OBJECT.isObject(item));

      if (dataItemIsObject && !this.settings().valueField) {
        throw new Error(
          'Value field is required for data binding in spreadsheet component.',
        );
      }

      if (
        (dataItemIsObject && this.settings().valueField) ||
        !dataItemIsObject
      ) {
        this.cells.update((cells) => {
          cells.forEach((cell, index) => {
            cell.dataItem = data?.at(index);
            cell.formattedValue = computed(() =>
              this.formatValue(
                cell.dataItem?.[this.settings().valueField!] as
                  string | number | undefined,
                this.settings().numberFormattingPrecision,
                cell.dataItem?.[this.settings().currencyField!] as
                  string | undefined,
              ),
            );
            let cellClasses = cell.classes();
            cell.classes = computed(
              () =>
                cellClasses +
                ' ' +
                cell.dataItem?.[this.settings().cellCssField!],
            );

            this.updateState(
              cell,
              cell.dataItem?.[this.settings().valueField!],
              cell.dataItem?.[this.settings().valueField!],
            );
            this.updateValidation(
              cell,
              cell.dataItem?.[this.settings().valueField!],
            );
          });
          return cells;
        });
      }
    }

    this.generateSummaryCells(
      this.settings().columnTitles!,
      this.settings().rowTitles!,
    );
  }

  private bindDataToSummaryCells(data: Array<T>) {
    if (data) {
      let dataItemIsObject = data?.some((item) => UTILS.OBJECT.isObject(item));

      if (dataItemIsObject && !this.settings().valueField) {
        throw new Error(
          'Value field is required for summary binding in spreadsheet component.',
        );
      }

      if (
        (dataItemIsObject && this.settings().valueField!) ||
        !dataItemIsObject
      ) {
        this.summaryCells.update((summaryCells) => {
          summaryCells.forEach((cell, index) => {
            cell.dataItem = data?.at(index);
            cell.formattedValue = computed(() =>
              this.formatValue(
                cell.dataItem?.[this.settings().valueField!] as
                  string | number | undefined,
                this.settings().numberFormattingPrecision,
                cell.dataItem?.[this.settings().currencyField!] as
                  string | undefined,
              ),
            );
          });
          return summaryCells;
        });
      }
    }
  }

  public refresh = () => {
    this.bindDataToCells(this.data());
  };

  protected commitEdit(value: string, cell: UiSpreadSheetCell<T>) {
    if (this.cancelledEditCell === cell) {
      this.cancelledEditCell = undefined;
      cell.editing = false;
      return;
    }
    this.updateValue(value, cell);
  }

  public updateValue(value: any, cell: UiSpreadSheetCell<T>, emit = true) {
    let plainValue = UTILS.OBJECT.isEmpty(value)
      ? undefined
      : this.settings().allowedValues !== SPREADSHEET_ALLOWED_VALUES.Numeric
        ? value
        : this.settings().numberFormattingType === 'percent' &&
            !this.settings().percentageAsIs
          ? +value / 100
          : +value;

    if (typeof plainValue === 'number' && !Number.isFinite(plainValue)) {
      cell.editing = false;
      return;
    }

    let currentValue = cell.dataItem?.[this.settings().valueField!];

    if (currentValue !== plainValue) {
      cell.dataItem![this.settings().valueField!] = plainValue;
      cell.dataItem![this.settings().deletedField!] = UTILS.OBJECT.isEmpty(
        value,
      ) as unknown as NonNullable<T>[keyof T];

      this.updateState(cell, currentValue, plainValue);
      this.updateValidation(cell, plainValue);

      if (emit) {
        this.valueChange.emit({
          values: [plainValue],
          dataItem: [cell.dataItem!],
        });
      }
    }
    cell.editing = false;
  }

  private updateState(
    cell: UiSpreadSheetCell<T>,
    pristineValue: unknown,
    currentValue: unknown,
  ) {
    if (this.stateService && !this.settings().stateless) {
      let column = this.settings().columnTitles![cell.columnIndex!];
      let row = this.settings().rowTitles![cell.rowIndex!];
      let cellUid = cell.dataItem?.[this.settings().idField!] ?? 0;
      let stateTag = `${this.tag()} ${column}-${row} value@${this.uid()}_${cellUid}`;

      if (!this.stateService.stateExists(stateTag)) {
        this.stateService.registerState(stateTag, pristineValue);
      }

      this.stateService.updateState(stateTag, currentValue);
    }
  }

  private updateValidation(cell: UiSpreadSheetCell<T>, value: unknown) {
    if (this.validationService) {
      let column = this.settings().columnTitles![cell.columnIndex!];
      let row = this.settings().rowTitles![cell.rowIndex!];
      let cellUid = cell.dataItem?.[this.settings().idField!] ?? 0;
      let validationTag = `${this.tag()} ${column}-${row} value@${this.uid()}_${cellUid}`;
      let cellValidators = this.cellValidators?.()?.(cell.dataItem);

      if (cellValidators?.length! > 0) {
        let validationExists =
          this.validationService.validationExists(validationTag);
        if (!validationExists) {
          let { errors, warnings } = this.validationService.registerValidation(
            validationTag,
            signal(value),
            cellValidators!,
          );
        }
        let { errors, warnings } = this.validationService.validate(
          validationTag,
          value,
        );

        if (!cell.state) {
          cell.state = linkedSignal(() => {
            cell.annotations =
              errors()?.length! > 0
                ? errors().join('\n\r')
                : warnings()?.length! > 0
                  ? warnings().join('\n\r')
                  : undefined;
            return errors()?.length! > 0
              ? StateEnum.Error
              : warnings()?.length! > 0
                ? StateEnum.Warning
                : (this.stateService?.getState(
                    `${this.tag()} ${column}-${row} value@${this.uid()}_${cellUid}`,
                  ) ?? StateEnum.Pristine);
          });
        }
      }
    }
  }

  protected deleteValues() {
    let deletedValues = <Array<unknown>>[];
    let deletedDataItems = <Array<T>>[];
    this.cells()?.forEach((cell: UiSpreadSheetCell<T>) => {
      if (cell.marked || cell.selected) {
        deletedValues.push(undefined);
        deletedDataItems.push(cell.dataItem!);
        this.updateValue(undefined, cell, false);
      }
    });
    if (deletedValues.length > 0) {
      this.valueChange.emit({
        values: deletedValues,
        dataItem: deletedDataItems,
      });
    }
  }

  protected formatValue(
    value: string | number | undefined,
    precision: string = '1.0-2',
    currency: string = 'USD',
  ) {
    let formattedValue = undefined;
    if (UTILS.OBJECT.isEmpty(value)) return formattedValue;

    switch (this.settings().numberFormattingType) {
      case 'currency':
        formattedValue = SpreadsheetComponent.currencyPipe.transform(
          value,
          currency,
          'symbol-narrow',
          precision,
        );
        break;
      case 'percent':
        formattedValue = SpreadsheetComponent.percentPipe.transform(
          this.settings()!.percentageAsIs && value !== undefined
            ? +value / 100
            : value,
          precision,
        );
        break;
      case 'ratio':
        formattedValue =
          SpreadsheetComponent.decimalPipe.transform(value, precision) + ' x';
        break;
      case 'abbreviated':
        formattedValue = UTILS.FORMAT.toAbbr(value as number, true, 2, ',');
        break;
      default:
        formattedValue = SpreadsheetComponent.decimalPipe.transform(
          value,
          precision,
        );
    }
    return formattedValue ?? undefined;
  }

  protected toggleFormat() {
    this.settings().numberFormat = !this.settings().numberFormat;
  }

  protected editCell(cell: IUiSpreadSheetCell<T>, value?: string) {
    /* if (
      cell.dataItem &&
      this.settings().valueField &&
      cell.dataItem![this.settings().valueField!] === undefined
    ) {
      cell.dataItem![this.settings().valueField!] = '';
    } */
    this.cancelledEditCell = undefined;
    cell.editing = true;
    setTimeout(() => {
      let input = cell.el?.firstElementChild;
      if (input instanceof HTMLInputElement) {
        input.focus();
        if (value !== undefined) {
          input.value = value;
        } else {
          let value = cell.dataItem?.[this.settings().valueField!];

          input.value = UTILS.OBJECT.isNil(value)
            ? ''
            : `${this.settings().numberFormattingType === 'percent' && !this.settings().percentageAsIs ? (value as number) * 100 : value}`;
        }
      }
    }, 10);
  }

  protected copy(e: Event) {
    if (e instanceof KeyboardEvent && e.ctrlKey) {
      const selectedCells = this.cells()?.filter(
        (item) => item.selected || item.marked,
      );
      selectedCells?.sort(
        (a, b) => a.rowIndex! - b.rowIndex! || a.columnIndex! - b.columnIndex!,
      );
      let copyStream = '';
      selectedCells?.forEach((cell: UiSpreadSheetCell<T>, index) => {
        copyStream =
          copyStream +
          `${cell.dataItem?.[this.settings().valueField!]}${
            !selectedCells[index + 1]
              ? ''
              : (selectedCells[index + 1] as UiSpreadSheetCell<T>)
                    .columnIndex! -
                    cell.columnIndex! ===
                  1
                ? ' '
                : '\n'
          }`;
      });
      navigator.clipboard.writeText(copyStream);
    }
    e.preventDefault();
  }

  protected paste(e: Event) {
    const rowSplit = '\n';
    const selectedCell = this.cells()?.find((item) => item.selected);

    if (selectedCell) {
      if (navigator.clipboard === undefined) return;
      navigator.clipboard.readText().then((clipText) => {
        let updatedValues = <Array<unknown>>[];
        const clipRows = clipText?.split(rowSplit).filter(Boolean);
        let rowItems;
        const colSplit = clipRows[0].includes('\t') ? '\t' : ' ';
        const dataItems: Array<T> = [];
        clipRows.forEach((row, rowIndex) => {
          rowItems = row?.split(colSplit);
          rowItems.forEach((value, colIndex) => {
            value = value.replace('\r', '');
            const item = this.cells()?.find(
              (item) =>
                item.columnIndex ===
                  (selectedCell as UiSpreadSheetCell<T>).columnIndex! +
                    colIndex &&
                (item as UiSpreadSheetCell<T>).rowIndex ===
                  (selectedCell as UiSpreadSheetCell<T>).rowIndex! + rowIndex,
            ) as PropExtend<UiSpreadSheetCell<T>>;
            if (item) {
              let plainValue =
                value.trim() === ''
                  ? undefined
                  : this.settings().allowedValues === 'numeric'
                    ? this.settings().numberFormattingType === 'percent' &&
                      !this.settings().percentageAsIs
                      ? +value * 100
                      : +value
                    : value;
              this.updateValue(plainValue ?? undefined, item, false);
              updatedValues.push(plainValue);
              item.el!.innerHTML =
                this.formatValue(
                  item.dataItem?.[this.settings().valueField!] as
                    string | number | undefined,
                ) ?? '';
              dataItems.push(item.dataItem!);
            }
          });
        });
        if (updatedValues.length > 0) {
          this.valueChange.emit({
            values: updatedValues,
            dataItem: dataItems,
          });
        }
      });
    }

    e.stopPropagation();
    e.preventDefault();
  }

  //#endregion

  //#region Key Events

  protected keydown(e: KeyboardEvent, cell: IUiSpreadSheetCell<T>) {
    const elementType: ElementType = (e.target as HTMLElement)
      .localName as ElementType;
    if (this.keyAllowed(e.key) && elementType && this.keymap[elementType]) {
      if (typeof this.keymap[elementType][e.key] === 'function') {
        this.keymap[elementType][e.key](this.firstMarkedCell, e);
      } else {
        this.keymap[elementType].Other(this.firstMarkedCell, e);
      }
    } else {
      e.preventDefault();
      return;
    }
  }

  //#endregion

  //#region Mouse Events

  protected click(e: Event, cell: IUiSpreadSheetCell<T>) {
    if (cell.editing) {
      e.preventDefault();
      return;
    }

    this.resetAllCells();
    cell.selected = true;

    this.updateMarked(
      cell.rowIndex! - (this.lastMarkedCell?.columnIndex ?? 0),
      cell.columnIndex! - (this.lastMarkedCell?.columnIndex ?? 0),
      e,
    );
  }

  protected clickHeader(
    e: MouseEvent,
    colIndex: number = -1,
    rowIndex: number = -1,
  ) {
    if (!(e.shiftKey || e.ctrlKey)) {
      this.resetAllCells();
    }

    const colItems = this.cells()?.filter(
      (item) =>
        ((item.columnIndex === colIndex || colIndex === -1) &&
          (item.rowIndex === rowIndex || rowIndex === -1)) ||
        (item.marked && (e.shiftKey || e.ctrlKey)),
    );
    this.firstMarkedCell = colItems?.at(0);
    this.firstMarkedCell!.selected = true;
    this.lastMarkedCell = colItems?.at(colItems.length - 1);
    colItems?.forEach((item) => (item.marked = true));
    this.cellSelected.emit(
      this.cells()?.filter((item) => item.marked || item.selected),
    );
  }

  protected startDrag(e: DragEvent, cell: IUiSpreadSheetCell<T>) {
    if (cell.editing) {
      e.preventDefault();
      return;
    }

    this.resetAllCells();

    cell.selected = true;
    this.firstMarkedCell = cell;

    const proxyEl = (e.target as HTMLElement)?.cloneNode(true) as HTMLElement;
    proxyEl.style.opacity = '0';
    e.dataTransfer!.setDragImage(proxyEl, 0, 0);
  }

  protected enterDrag(cell: IUiSpreadSheetCell<T>) {
    this.lastMarkedCell = cell;
    this.cells()?.forEach(
      (item) =>
        (item.marked = this.shouldBeMarked(item.columnIndex!, item.rowIndex!)),
    );
  }

  protected endDrag() {
    this.cellSelected.emit(
      this.cells()?.filter((item) => item.marked || item.selected),
    );
  }

  //#endregion

  //#region Private Methods

  private updateMarked(
    rowOffset: number,
    columnOffset: number,
    e: Event,
    forceFocus?: boolean,
  ) {
    const toItem = this.cells()?.find(
      (item) =>
        item.columnIndex ===
          (this.lastMarkedCell?.columnIndex ?? 0) + columnOffset &&
        item.rowIndex === (this.lastMarkedCell?.rowIndex ?? 0) + rowOffset,
    );
    if (toItem) {
      if ((e as KeyboardEvent).shiftKey) {
        this.lastMarkedCell = toItem;
        this.cells()?.forEach(
          (item) =>
            (item.marked = this.shouldBeMarked(
              item.columnIndex!,
              item.rowIndex!,
            )),
        );
      } else if ((e as KeyboardEvent).ctrlKey) {
        this.resetSelectedCell();
        this.firstMarkedCell = this.lastMarkedCell = toItem;
        toItem.selected = toItem.marked = true;
      } else {
        this.resetAllCells();
        toItem.selected = true;
        this.firstMarkedCell = this.lastMarkedCell = toItem;
      }
      this.cellSelected.emit(
        this.cells()?.filter((item) => item.marked || item.selected),
      );
    }

    let selectedCell = this.cells()?.find((item) => item.selected);
    this.cells()?.forEach(
      (item, i) =>
        (item.el = this.uiValueRef.nativeElement.querySelector(
          `td[data-index="${i}"]`,
        ) as HTMLElement),
    );
    let td = this.uiValueRef.nativeElement.querySelector(
      `td[data-index="${this.indexFromColumnRow(
        selectedCell!.columnIndex!,
        selectedCell!.rowIndex!,
      )}"]`,
    ) as HTMLElement;
    td?.focus();
    e.preventDefault();
  }

  private undoEdit(input: HTMLInputElement, cell: IUiSpreadSheetCell<T>) {
    this.cancelledEditCell = cell;
    cell.editing = false;
    if (cell.el) {
      cell.el.focus();
    } else {
      input.blur();
    }
  }

  private multiplyValue(
    e: KeyboardEvent,
    cell: IUiSpreadSheetCell<T>,
    multiplier: string,
  ) {
    const el = e.target as HTMLInputElement;
    if (this.settings().allowedValues === 'numeric') {
      const multiFactor =
        UTILS.CONSTANTS.SHORTCUT_NUMBER_MULTIPLIERS[multiplier];
      if (multiFactor === undefined) return;
      el.value = (+el.value * multiFactor).toString();
      e.preventDefault();
    }
    return;
  }

  private resetAllCells() {
    this.cells().forEach((item) => {
      item.selected = false;
      item.marked = false;
      item.editing = false;
    });
    this.firstMarkedCell = this.lastMarkedCell = undefined;
  }

  private resetSelectedCell() {
    this.cells()?.forEach((item) => (item.selected = false));
    this.firstMarkedCell = this.lastMarkedCell = undefined;
  }

  private keyAllowed(key: string) {
    let isAllowed =
      allowedKeys.includes(key) || this.allowedValueRegExp.test(key);
    return isAllowed;
  }

  protected indexFromColumnRow(columnIndex: number, rowIndex: number) {
    return this.settings().columnTitles!.length * rowIndex + columnIndex;
  }

  protected shouldBeMarked(columnIndex: number, rowIndex: number) {
    return (
      (columnIndex! <=
        Math.max(
          this.firstMarkedCell?.columnIndex!,
          this.lastMarkedCell?.columnIndex!,
        ) ||
        columnIndex === -1) &&
      (columnIndex! >=
        Math.min(
          this.firstMarkedCell?.columnIndex!,
          this.lastMarkedCell?.columnIndex!,
        ) ||
        columnIndex === -1) &&
      (rowIndex! <=
        Math.max(
          this.firstMarkedCell?.rowIndex!,
          this.lastMarkedCell?.rowIndex!,
        ) ||
        rowIndex === -1) &&
      (rowIndex! >=
        Math.min(
          this.firstMarkedCell?.rowIndex!,
          this.lastMarkedCell?.rowIndex!,
        ) ||
        rowIndex === -1)
    );
  }

  //#endregion
}
